<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { QUOTE_VERTICAL_META } from '$lib/types';
  import type { Quotation } from '$lib/types';
  import Pagination from '$lib/components/ui/Pagination.svelte';

  const {
    search,
    vertical,
    designerLabel,
    onOpen
  }: {
    search: string;
    vertical: string;
    designerLabel: (q: Quotation) => string;
    onOpen: (id: number) => void;
  } = $props();

  const PER_PAGE = 30;
  let page = $state(1);
  let restoringID = $state<number | null>(null);
  const queryClient = useQueryClient();

  $effect(() => {
    void search;
    void vertical;
    page = 1;
  });

  const query = createQuery(() => ({
    queryKey: ['archived-quotations', vertical, search, page],
    queryFn: () =>
      quotationsApi.list({
        archived: '1',
        vertical: vertical === 'all' ? undefined : vertical,
        q: search || undefined,
        page,
        per_page: PER_PAGE
      })
  }));

  async function restore(q: Quotation) {
    restoringID = q.id;
    try {
      await quotationsApi.unarchive(q.id);
      toast.success('Quotation restored to the active pipeline.');
      await queryClient.invalidateQueries({ queryKey: ['archived-quotations'] });
      await queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] });
      await queryClient.invalidateQueries({ queryKey: ['approved-quotations'] });
      await queryClient.invalidateQueries({ queryKey: ['approved-quotations-count'] });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      restoringID = null;
    }
  }
</script>

<div class="flex-1 overflow-y-auto px-4 lg:px-7 pb-6">
  {#if query.isLoading}
    <p class="tym-loading-state">Loading archived quotations&hellip;</p>
  {:else if query.isError}
    <p class="tym-error-state">{getErrorMessage(query.error)}</p>
  {:else}
    <div class="tym-table-wrap tym-table-scroll">
      <table class="tym-table">
        <thead>
          <tr>
            <th>Number</th>
            <th>Client</th>
            <th>Project</th>
            <th>Lot</th>
            <th>Vertical</th>
            <th>Archived</th>
            <th>Designer</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each query.data?.results ?? [] as q (q.id)}
            <tr class="clickable" onclick={() => onOpen(q.id)}>
              <td class="font-mono text-[12.5px]">{q.quotation_number}</td>
              <td class="font-medium text-navy">{q.client_name || 'Untitled client'}</td>
              <td class="muted">{q.project_name || 'Untitled project'}</td>
              <td class="muted whitespace-nowrap">{q.lot_label}</td>
              <td>
                <span class="tym-badge text-toyama-teal bg-toyama-teal/10">
                  {QUOTE_VERTICAL_META[q.vertical]?.label ?? q.vertical}
                </span>
              </td>
              <td class="muted">{q.archived_at ? new Date(q.archived_at).toLocaleDateString() : '—'}</td>
              <td class="muted">{designerLabel(q)}</td>
              <td>
                <button
                  type="button"
                  class="btn-secondary !py-1.5 !px-2.5 text-[12px]"
                  disabled={restoringID === q.id}
                  onclick={(e) => {
                    e.stopPropagation();
                    restore(q);
                  }}
                >
                  {restoringID === q.id ? 'Restoring…' : 'Restore'}
                </button>
              </td>
            </tr>
          {:else}
            <tr><td class="empty" colspan="8">No archived quotations{search ? ' match your search' : ''}.</td></tr>
          {/each}
        </tbody>
      </table>
    </div>
    <Pagination page={query.data?.page ?? page} perPage={PER_PAGE} count={query.data?.count ?? 0} onChange={(p) => (page = p)} />
  {/if}
</div>
